import axios from "axios";
import Cookies from "js-cookie";
import { 
  getDecryptedCookie, 
  removeEncryptedCookie,
  setEncryptedCookie,
  isValidEncryptedCookie 
} from '../utils/cookieEncryption';

// Create a logout function that can be triggered from anywhere
const logoutUser = () => {
  removeEncryptedCookie("_at");
  removeEncryptedCookie("_rt");
  Cookies.remove("u_id");
  Cookies.remove("from");
  Cookies.remove("f_log");
  Cookies.remove("i_id");
  Cookies.remove("i_nm");
  Cookies.remove("r_id");
  
  console.warn("User logged out due to unauthorized access");
};

const getAuthToken = () => {
  try {
    const decryptedToken = getDecryptedCookie("_at");
    return decryptedToken;
  } catch (error) {
    console.error("Error retrieving auth token:", error);
    return null;
  }
};

// Create axios instance - browser compatible
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000, // 15 second timeout
});

// Endpoints that don't require authentication
const excludedEndpoints = [
  "Authentication/login",
  "Common/GetStates",
  "Common/GetCities",
];

// Request interceptor - adds auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const isExcluded = excludedEndpoints.some((endpoint) =>
      config.url.includes(endpoint)
    );

    if (!isExcluded && token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handles errors and retries
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    console.error("API error details:", {
      status: error.response?.status,
      code: error.code,
      message: error.message,
      url: originalRequest?.url
    });

    // Retry logic for network/protocol errors (but only once)
    if ((error.code === 'ERR_NETWORK' || 
         error.code === 'ECONNABORTED' ||
         error.message?.includes('HTTP2') || 
         error.message?.includes('protocol') ||
         error.message?.includes('ERR_HTTP2')) && 
        !originalRequest?._retry) {
      
      if (originalRequest) {
        originalRequest._retry = true;        
        // Wait 2 seconds before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        try {
          return await apiClient(originalRequest);
        } catch (retryError) {
          // If retry fails with 401, clear cookies
          if (retryError.response?.status === 401 || 
              retryError.message?.includes('401') ||
              retryError.message?.includes('Unauthorized')) {
            logoutUser();
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('auth:unauthorized'));
            }
          }
          return Promise.reject(retryError);
        }
      }
    }

    // Handle 401 Unauthorized or any auth-related errors
    const isAuthError = 
      error.response?.status === 401 ||
      error.message?.includes('401') ||
      error.message?.includes('Unauthorized') ||
      error.message?.includes('token expired') ||
      error.message?.includes('invalid token');

    if (isAuthError) {
      logoutUser();
      
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }

    return Promise.reject(error);
  }
);

// Export the logout function for use in components
export const logoutUserFn = logoutUser;

// Helper function to handle API errors consistently
const handleApiError = (error) => {
  // Check for auth errors that might not be caught by interceptor
  const isAuthError = 
    error.response?.status === 401 ||
    error.message?.includes('401') ||
    error.message?.includes('Unauthorized') ||
    error.code === 'ERR_NETWORK' ||
    error.message?.includes('HTTP2') ||
    error.message?.includes('protocol');

  if (isAuthError) {
    logoutUser();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }

  // Extract meaningful error message
  const errorMessage = 
    error.response?.data?.message || 
    error.response?.data?.Message ||
    error.message || 
    "An unexpected error occurred.";
  
  return { isError: true, message: errorMessage };
};

// GET request with fallback to fetch for problematic endpoints
export const getData = async (endpoint, responseType = "json") => {
  try {
    const response = await apiClient.get(endpoint, { responseType });
    return responseType === "blob" ? response : response.data;
  } catch (error) {
    // If it's a protocol error, try with fetch as fallback
    if (error.message?.includes('HTTP2') || 
        error.message?.includes('protocol') ||
        error.code === 'ERR_NETWORK') {
            
      try {
        const token = getAuthToken();
        const fullUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;
        
        const fetchResponse = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          signal: AbortSignal.timeout(15000) // 15 second timeout
        });
        
        // Check for 401
        if (fetchResponse.status === 401) {
          logoutUser();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return { isError: true, message: "Unauthorized - Please login again" };
        }
        
        if (!fetchResponse.ok) {
          throw new Error(`HTTP error! status: ${fetchResponse.status}`);
        }
        
        const data = await fetchResponse.json();
        return data;
        
      } catch (fetchError) {
        console.error("Fetch fallback also failed:", fetchError);
        // If fetch also fails, check if it's an auth error
        if (fetchError.message?.includes('401') || fetchError.message?.includes('Unauthorized')) {
          logoutUser();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
        }
        return handleApiError(fetchError);
      }
    }
    
    return handleApiError(error);
  }
};

// POST request
export const postData = async (endpoint, body) => {
  try {
    const response = await apiClient.post(endpoint, body);
    return response.data;
  } catch (error) {
    // Try fetch fallback for protocol errors
    if (error.message?.includes('HTTP2') || 
        error.message?.includes('protocol') ||
        error.code === 'ERR_NETWORK') {
            
      try {
        const token = getAuthToken();
        const fullUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;
        
        const fetchResponse = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(body),
          signal: AbortSignal.timeout(15000)
        });
        
        if (fetchResponse.status === 401) {
          logoutUser();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return { isError: true, message: "Unauthorized - Please login again" };
        }
        
        const data = await fetchResponse.json();
        return data;
        
      } catch (fetchError) {
        return handleApiError(fetchError);
      }
    }
    
    return handleApiError(error);
  }
};

// PUT request
export const putData = async (endpoint, body) => {
  try {
    const response = await apiClient.put(endpoint, body);
    return response.data;
  } catch (error) {
    // Try fetch fallback for protocol errors
    if (error.message?.includes('HTTP2') || 
        error.message?.includes('protocol') ||
        error.code === 'ERR_NETWORK') {
            
      try {
        const token = getAuthToken();
        const fullUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;
        
        const fetchResponse = await fetch(fullUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(body),
          signal: AbortSignal.timeout(15000)
        });
        
        if (fetchResponse.status === 401) {
          logoutUser();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return { isError: true, message: "Unauthorized - Please login again" };
        }
        
        const data = await fetchResponse.json();
        return data;
        
      } catch (fetchError) {
        return handleApiError(fetchError);
      }
    }
    
    return handleApiError(error);
  }
};

// DELETE request
export const deleteData = async (endpoint) => {
  try {
    const response = await apiClient.delete(endpoint);
    return response.data;
  } catch (error) {
    // Try fetch fallback for protocol errors
    if (error.message?.includes('HTTP2') || 
        error.message?.includes('protocol') ||
        error.code === 'ERR_NETWORK') {
            
      try {
        const token = getAuthToken();
        const fullUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;
        
        const fetchResponse = await fetch(fullUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          signal: AbortSignal.timeout(15000)
        });
        
        if (fetchResponse.status === 401) {
          logoutUser();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return { isError: true, message: "Unauthorized - Please login again" };
        }
        
        const data = await fetchResponse.json();
        return data;
        
      } catch (fetchError) {
        return handleApiError(fetchError);
      }
    }
    
    return handleApiError(error);
  }
};

export default {
  getData,
  postData,
  putData,
  deleteData,
  logoutUserFn
};